import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { FEATURES } from "./utils/environment";
import Welcome from "./pages/Welcome";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Profile from "./pages/Profile";
import EditProfile from "./pages/EditProfile";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Market from "./pages/Market";
import AddListing from "./pages/AddListing";
import ViewMyListings from "./pages/ViewMyListings";
import EditListing from "./pages/EditListing";
import ListingDetails from "./pages/ListingDetails";
import Messages from "./pages/Messages";
import DirectPage from "./pages/DirectPage";
import NotificationsPage from "./pages/NotificationsPage";
import CalendarPage from "./pages/CalendarPage";
import VerifyEmail from "./pages/VerifyEmail";
// import DebugFirebase from "./pages/DebugFirebase"; // Commented out - not used
import Transactions from "./pages/Transactions";
import SellerSuccess from "./pages/SellerSuccess";
import SellerRetry from "./pages/SellerRetry";
import SellerDashboard from "./pages/SellerDashboard";
import SellerOnboardingPage from "./pages/SellerOnboardingPage";

// Info Pages
import HowItWorks from "./pages/InfoPages/HowItWorks";
import SafetyGuidelines from "./pages/InfoPages/SafetyGuidelines";
import SellerResources from "./pages/InfoPages/SellerResources";
import FAQ from "./pages/InfoPages/FAQ";
import TermsOfService from "./pages/InfoPages/TermsOfService";
import PrivacyPolicy from "./pages/InfoPages/PrivacyPolicy";
import CommunityGuidelines from "./pages/InfoPages/CommunityGuidelines";
import DisputeResolution from "./pages/InfoPages/DisputeResolution";
import HelpCenter from "./pages/InfoPages/HelpCenter";
import ContactUs from "./pages/InfoPages/ContactUs";
import ReportIssue from "./pages/InfoPages/ReportIssue";
import Accessibility from "./pages/InfoPages/Accessibility";
import SocialMedia from "./pages/InfoPages/SocialMedia";
import AboutUs from "./pages/InfoPages/AboutUs";
import Partnerships from "./pages/InfoPages/Partnerships";

import { auth, analytics, logEvent } from "./services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { COLORS } from "./constants";

// Analytics Page View Tracker Component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_path: location.pathname,
        page_title: document.title
      });
    }
  }, [location]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      // CRITICAL: Email verification is REQUIRED in all environments
      // The only exception is ACTIVE emulator development (not just localhost)
      const verified = currentUser && currentUser.emailVerified;
      
      setUser(currentUser);
      setIsVerified(!!verified);
      setAuthChecked(true);
      
      // In emulator mode, mark as verified for testing purposes
      if (FEATURES.USE_FIREBASE_EMULATORS && currentUser) {
        setIsVerified(true);
      }
    });
    return () => unsubscribe();
  }, []);

  if (!authChecked) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: COLORS.grayLight,
        padding: '20px',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${COLORS.border}`,
          borderTop: `3px solid ${COLORS.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '24px',
        }} />
        <p style={{
          fontSize: '16px',
          color: COLORS.text.secondary,
          textAlign: 'center',
          margin: 0,
        }}>
          Loading authentication...
        </p>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AnalyticsTracker />
      <ScrollToTop />
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: COLORS.grayLight,
      }}>
        <Navbar user={isVerified ? user : null} needsVerify={user && !user.emailVerified} />
        
        <main style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<Welcome />} />
            <Route path="/profile" element={isVerified ? <Profile user={user} /> : <Navigate to="/login" />} />
            <Route path="/edit-profile" element={isVerified ? <EditProfile user={user} /> : <Navigate to="/login" />} />
            <Route path="/login" element={isVerified ? <Navigate to="/market" /> : <Login />} />
            <Route path="/signup" element={isVerified ? <Navigate to="/market" /> : <Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/market" element={isVerified ? <Market /> : <Navigate to="/login" />} />
            <Route path="/add-listing" element={isVerified ? <AddListing /> : <Navigate to="/login" />} />
            <Route path="/my-listings" element={isVerified ? <ViewMyListings /> : <Navigate to="/login" />} />
            <Route path="/edit-listing/:id" element={isVerified ? <EditListing /> : <Navigate to="/login" />} />
            <Route path="/listing/:id" element={isVerified ? <ListingDetails /> : <Navigate to="/login" />} />
            <Route path="/direct" element={isVerified ? <Messages /> : <Navigate to="/login" />} />
            <Route path="/direct/:threadId" element={isVerified ? <DirectPage /> : <Navigate to="/login" />} />
            <Route path="/notifications" element={isVerified ? <NotificationsPage /> : <Navigate to="/login" />} />
            <Route path="/calendar" element={isVerified ? <CalendarPage /> : <Navigate to="/login" />} />
            <Route path="/transactions" element={isVerified ? <Transactions /> : <Navigate to="/login" />} />
            <Route path="/seller-success" element={isVerified ? <SellerSuccess /> : <Navigate to="/login" />} />
            <Route path="/seller-retry" element={isVerified ? <SellerRetry /> : <Navigate to="/login" />} />
            <Route path="/seller-dashboard" element={isVerified ? <SellerDashboard /> : <Navigate to="/login" />} />
            <Route path="/seller-onboarding" element={isVerified ? <SellerOnboardingPage /> : <Navigate to="/login" />} />
            
            {/* Info Pages - Publicly accessible */}
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/safety-guidelines" element={<SafetyGuidelines />} />
            <Route path="/seller-resources" element={<SellerResources />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/community-guidelines" element={<CommunityGuidelines />} />
            <Route path="/dispute-resolution" element={<DisputeResolution />} />
            <Route path="/help-center" element={<HelpCenter />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/report-issue" element={<ReportIssue />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/social-media" element={<SocialMedia />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/partnerships" element={<Partnerships />} />
          </Routes>
        </main>
        
        <Footer />
      </div>
    </Router>
  );
}

export default App;